import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookF,
  faTwitter,
  faInstagram,
  faLinkedinIn,
} from "@fortawesome/free-brands-svg-icons";
import {
  faPhoneAlt,
  faEnvelope,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import { Helmet } from "react-helmet-async";

const ContactsPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendWhatsAppMessage = (e) => {
    e.preventDefault();

    // Validate all fields
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      alert("Please fill in all fields.");
      return;
    }

    const businessPhone = "27681247577"; // Your WhatsApp business number

    // Construct the message with all details
    const userMessage = `Hello, I'm ${formData.name} (${formData.email}). Subject: ${formData.subject}. Message: ${formData.message}`;
    const encodedMessage = encodeURIComponent(userMessage);

    // Detect mobile vs desktop for correct WhatsApp link
    const isMobile = /iPhone|Android|iPad|iPod/i.test(navigator.userAgent);
    let url = `https://wa.me/${businessPhone}?text=${encodedMessage}`;
    if (!isMobile) {
      url = `https://web.whatsapp.com/send?phone=${businessPhone}&text=${encodedMessage}`;
    }

    // Open WhatsApp in a new tab/window
    window.open(url, "_blank");

    // Optional: reset form after sending
    setFormData({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <>
      <Helmet>
        <title>Contact Us, SPK Academy & Publishing</title>
        <meta
          name="description"
          content="Get in touch with SPK Academy & Publishing. Call us, email, or send a message via WhatsApp. We're here to help with your educational and creative needs."
        />
        <meta property="og:title" content="Contact Us, SPK Academy & Publishing" />
        <meta
          property="og:description"
          content="Reach out to SPK Academy for inquiries about books, classes, publishing, or any of our services."
        />
        <meta property="og:url" content="https://spkrobertacademy.com/contacts" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="py-12">
        <div className="container mx-auto px-2">
          {/* Header */}
          <div className="bg-gray-300 py-4 rounded-xl text-center mb-3 sm:mb-3">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Contact Us</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Have questions or want to work together? Reach out to us and we'll get back to you as soon as possible.
            </p>
          </div>

          {/* Contact Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-3">
            {/* Left Column - Contact Info */}
            <div className="bg-white rounded-xl shadow-lg px-2 py-4 sm:p-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center sm:text-left">Get in Touch</h2>

              {/* Contact Details */}
              <div className="space-y-6">
                {/* Phone */}
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <FontAwesomeIcon icon={faPhoneAlt} className="text-blue-600 w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Phone</h3>
                    <p className="text-gray-600">(+27) 84 611 4384</p>
                    <p className="text-gray-600">(+27) 68 124 7577</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <FontAwesomeIcon icon={faEnvelope} className="text-blue-600 w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Email</h3>
                    <a
                      href="mailto:secretariatebusiness@gmail.com"
                      className="text-gray-600 hover:text-blue-600 transition"
                    >
                      secretariatebusiness@gmail.com
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-blue-600 w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Address</h3>
                    <p className="text-gray-600">1667 Kameeldoorn Street</p>
                    <p className="text-gray-600">Hoopstad 9479</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-8">
                <h3 className="font-semibold text-gray-800 mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-200 p-2 sm:p-3 rounded-full hover:bg-blue-600 hover:text-white transition"
                  >
                    <FontAwesomeIcon icon={faFacebookF} className="w-4 h-4" />
                  </a>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-200 p-3 rounded-full hover:bg-blue-400 hover:text-white transition"
                  >
                    <FontAwesomeIcon icon={faTwitter} className="w-4 h-4" />
                  </a>
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-200 p-3 rounded-full hover:bg-pink-600 hover:text-white transition"
                  >
                    <FontAwesomeIcon icon={faInstagram} className="w-4 h-4" />
                  </a>
                  <a
                    href="https://linkedin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-200 p-3 rounded-full hover:bg-blue-700 hover:text-white transition"
                  >
                    <FontAwesomeIcon icon={faLinkedinIn} className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Simple Map Placeholder */}
              <div className="mt-8 h-48 bg-gray-300 rounded-lg overflow-hidden">
                <iframe
                  title="map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3474.233456789012!2d25.123456!3d-28.123456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjnCsDA3JzI0LjQiUyAyNcKwMDcnMjQuNCJF!5e0!3m2!1sen!2sza!4v1620000000000!5m2!1sen!2sza"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div className="bg-white rounded-xl shadow-lg px-2 py-4 sm:p-4">
              <h2 className="text-2xl font-semibold text-gray-800 mb-6">Send a Message</h2>
              <form onSubmit={sendWhatsAppMessage}>
                {/* Name */}
                <div className="mb-4">
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    placeholder="John Doe"
                  />
                </div>

                {/* Email */}
                <div className="mb-4">
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    placeholder="you@example.com"
                  />
                </div>

                {/* Subject */}
                <div className="mb-4">
                  <label htmlFor="subject" className="block text-gray-700 font-medium mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    placeholder="How can we help?"
                  />
                </div>

                {/* Message */}
                <div className="mb-4">
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none"
                    placeholder="Tell us more about your inquiry..."
                  ></textarea>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white font-semibold py-3 px-2 sm:px-6 rounded-lg hover:bg-blue-700 transition duration-200"
                >
                  Send via WhatsApp
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactsPage;